import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { ChatBubbleOutline as CommentsIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getPlatformName } from '../config/platforms';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Comment {
  id: string;
  text: string;
  at: string | null;
  author: string | null;
  likes: number | null;
  replies: Comment[];
}

interface NetworkComments {
  platform: string;
  status: 'ok' | 'needs_permission' | 'unsupported' | 'error';
  message?: string;
  comments?: Comment[];
}

const when = (at: string | null) =>
  at ? new Date(at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

const CommentItem: React.FC<{ c: Comment; reply?: boolean }> = ({ c, reply }) => (
  <Box sx={{ pl: reply ? 2 : 0, borderLeft: reply ? 2 : 0, borderColor: 'divider', mt: 1 }}>
    <Typography variant="caption" color="text.secondary">
      {/* Instagram withholds who commented without further Meta review */}
      {c.author || 'Someone'} · {when(c.at)}{c.likes ? ` · ${c.likes} like${c.likes === 1 ? '' : 's'}` : ''}
    </Typography>
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.text}</Typography>
    {c.replies.map(r => <CommentItem key={r.id} c={r} reply />)}
  </Box>
);

/**
 * The comments on a published post, read live when asked for (2026-10-17).
 *
 * Read-only by Ian's choice — reply on the network itself. Loaded on a click rather than when
 * the dialog opens: it is a live call to each network, and most opens are not for this.
 */
export const PostComments: React.FC<{ postId: string }> = ({ postId }) => {
  const { token } = useAuth();
  const [networks, setNetworks] = useState<NetworkComments[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}/comments`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(String(res.status));
      setNetworks((await res.json()).networks);
    } catch {
      setError('Could not load the comments.');
    } finally {
      setLoading(false);
    }
  };

  if (!networks) {
    return (
      <Box>
        <Button size="small" startIcon={loading ? <CircularProgress size={14} /> : <CommentsIcon />} onClick={load} disabled={loading}>
          Show comments
        </Button>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </Box>
    );
  }

  if (!networks.length) {
    return <Typography variant="body2" color="text.secondary">This post has not gone out anywhere yet.</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {networks.map(n => (
        <Box key={n.platform}>
          <Typography variant="subtitle2">{getPlatformName(n.platform)}</Typography>
          {n.status === 'ok' ? (
            n.comments?.length
              ? n.comments.map(c => <CommentItem key={c.id} c={c} />)
              : <Typography variant="body2" color="text.secondary">No comments.</Typography>
          ) : (
            <Typography variant="body2" color={n.status === 'error' ? 'error' : 'text.secondary'}>{n.message}</Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};
